import { useState } from "react";
import { toast } from "sonner";
import { useContent, useContentRefresh } from "../content-context";
import PageVideoCard from "./PageVideoCard";

import API from "../api";

const f = (path, label, type = "text") => ({ path, label: label || path.split(".").pop(), type });

const SECTIONS = [
  {
    key: "site",
    label: "Contact details",
    fields: [
      f("phoneDisplay", "Phone shown on the site"),
      f("email", "Enquiry email address"),
      f("address", "Address", "textarea"),
    ],
  },
  {
    key: "marquee",
    label: "Scrolling motto",
    fields: [f("0", "Phrase 1"), f("1", "Phrase 2"), f("2", "Phrase 3")],
  },
  {
    key: "hero",
    label: "Hero — top of the homepage",
    fields: [
      f("eyebrow", "Small line above the headline"),
      f("titleLines.0.text", "Headline — line 1"),
      f("titleLines.1.text", "Headline — line 2 (gold italic)"),
      f("titleLines.2.text", "Headline — line 3"),
      f("tagline", "Tagline"),
      f("body", "Welcome paragraph", "textarea"),
    ],
  },
  {
    key: "island",
    label: "The Island",
    fields: [
      f("heading", "Heading"),
      f("body1", "Paragraph 1", "textarea"),
      f("body2", "Paragraph 2", "textarea"),
      f("stats.0.value", "Stat 1 — number"),
      f("stats.0.label", "Stat 1 — label"),
      f("stats.1.value", "Stat 2 — number"),
      f("stats.1.label", "Stat 2 — label"),
      f("stats.2.value", "Stat 3 — number"),
      f("stats.2.label", "Stat 3 — label"),
    ],
  },
  {
    key: "accommodation",
    label: "The Lodge",
    fields: [
      f("heading", "Heading"),
      f("lead", "Intro line", "textarea"),
      ...[0, 1, 2, 3, 4, 5, 6].map((i) => f(`features.${i}`, `Feature ${i + 1}`)),
    ],
  },
  {
    key: "experiences",
    label: "Experience cards",
    repeat: true,
    fields: [f("title", "Title"), f("copy", "Description", "textarea")],
  },
  {
    key: "celebrations",
    label: "Celebrations cards",
    repeat: true,
    fields: [f("title", "Title"), f("copy", "Description", "textarea")],
  },
  {
    key: "traitors",
    label: "Traitors Island",
    fields: [
      f("eyebrow", "Eyebrow"),
      f("heading", "Big line"),
      f("copy", "Description", "textarea"),
      f("cta", "Button text"),
    ],
  },
  {
    key: "testimonials",
    label: "Guest reviews",
    repeat: true,
    fields: [f("quote", "Quote", "textarea"), f("name", "Guest name"), f("source", "Source (e.g. Tripadvisor)")],
  },
  {
    key: "contact",
    label: "Enquiry section",
    fields: [f("heading", "Heading"), f("lead", "Intro line", "textarea")],
  },
  {
    key: "packages",
    label: "Packages & Prices",
    fields: [
      f("heroEyebrow", "Page — small line above the headline"),
      f("heroH1", "Page — headline"),
      f("heroParas.0", "Page — intro paragraph 1", "textarea"),
      f("heroParas.1", "Page — intro paragraph 2", "textarea"),
      f("pkgEyebrow", "Package 1 — small line"),
      f("pkgH2", "Package 1 — heading"),
      f("pkgPrice", "Package 1 — big price (e.g. €3,300)"),
      f("pkgPriceNote", "Package 1 — price note (e.g. From €3,300 for two nights)"),
      ...[0, 1, 2, 3, 4].map((i) => f(`pkgIncludes.${i}`, `Package 1 — includes line ${i + 1}`)),
      f("pkg2Eyebrow", "Package 2 — small line"),
      f("pkg2H2", "Package 2 — heading"),
      f("pkg2Price", "Package 2 — big price (e.g. €4,999)"),
      f("pkg2PriceNote", "Package 2 — price note"),
      f("pkg2Badge", "Package 2 — badge (e.g. All-inclusive)"),
      f("pkg2Note", "Package 2 — line under the price", "textarea"),
      ...[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => f(`pkg2Includes.${i}`, `Package 2 — includes line ${i + 1}`)),
      f("pkg2Cta", "Package 2 — button text"),
      f("pkg3Title", "Package 3 — name (Traitors Island)"),
      f("pkg3Price", "Package 3 — price (e.g. €3,800)"),
      f("pkg3PriceNote", "Package 3 — price note", "textarea"),
      f("pkg3Copy", "Package 3 — description", "textarea"),
      f("pkg4Title", "Package 4 — name (Corporate)"),
      f("pkg4Price", "Package 4 — price (or 'Price on Enquiry')"),
      f("pkg4Cta", "Package 4 — button text"),
      f("pkg4PriceNote", "Package 4 — price note"),
      f("pkg4Copy", "Package 4 — description", "textarea"),
      f("pkg5Title", "Package 5 — name (Weddings)"),
      f("pkg5Price", "Package 5 — price (e.g. From €10,800)"),
      f("pkg5PriceNote", "Package 5 — price note", "textarea"),
      f("pkg5Copy", "Package 5 — description", "textarea"),
      f("pkg6Title", "Package 6 — name (Birthdays)"),
      f("pkg6Price", "Package 6 — price (e.g. From €3,300)"),
      f("pkg6PriceNote", "Package 6 — price note"),
      f("pkg6Copy", "Package 6 — description", "textarea"),
      f("extrasHeading", "Extras — heading"),
      ...[0, 1, 2, 3, 4].map((i) => f(`extras.${i}.label`, `Extra ${i + 1} — name`)),
      ...[0, 1, 2, 3, 4].map((i) => f(`extras.${i}.price`, `Extra ${i + 1} — price`)),
      f("extrasNote", "Extras — small note under the price list", "textarea"),
      f("ferryTitle", "Ferry — heading"),
      f("ferryPrice", "Ferry — price"),
      f("ferryNote", "Ferry — details", "textarea"),
      f("advTitle", "Adventure package — heading"),
      f("advBadge", "Adventure package — badge"),
      f("advPrice", "Adventure package — price (e.g. €50)"),
      f("advUnit", "Adventure package — unit (per person)"),
      f("advNormal", "Adventure package — normal price (e.g. €85)"),
      f("advSaving", "Adventure package — saving line"),
      ...[0, 1, 2].map((i) => f(`advIncludes.${i}`, `Adventure package — includes line ${i + 1}`)),
      f("bookingTitle", "Booking — heading"),
      ...[0, 1, 2].map((i) => f(`bookingNotes.${i}`, `Booking — line ${i + 1}`)),
      f("ctaHeading", "Bottom — heading"),
      f("ctaLead", "Bottom — paragraph", "textarea"),
      f("ctaLabel", "Bottom — button text"),
    ],
  },
  {
    key: "galleryPage",
    label: "Gallery page text",
    fields: [
      f("heroEyebrow", "Small line above the headline"),
      f("heroH1", "Headline"),
      f("heroParas.0", "Intro paragraph 1", "textarea"),
      f("heroParas.1", "Intro paragraph 2", "textarea"),
      f("ctaHeading", "Bottom — heading"),
      f("ctaLead", "Bottom — paragraph", "textarea"),
      f("ctaLabel", "Bottom — button text"),
    ],
  },
  {
    key: "about",
    label: "About page",
    fields: [
      f("heroEyebrow", "Small line above the headline"),
      f("heroH1", "Headline"),
      ...[0, 1, 2].map((i) => f(`heroParas.${i}`, `Intro paragraph ${i + 1}`, "textarea")),
      ...[0, 1, 2, 3].flatMap((i) => [
        f(`sections.${i}.eyebrow`, `Section ${i + 1} — small line`),
        f(`sections.${i}.h2`, `Section ${i + 1} — heading`),
        f(`sections.${i}.paras.0`, `Section ${i + 1} — paragraph 1`, "textarea"),
        f(`sections.${i}.paras.1`, `Section ${i + 1} — paragraph 2`, "textarea"),
        ...(i === 1 || i === 2 ? [f(`sections.${i}.paras.2`, `Section ${i + 1} — paragraph 3`, "textarea")] : []),
      ]),
      f("storyEyebrow", "Our Story — small line"),
      f("storyH2", "Our Story — heading"),
      ...[0, 1].map((i) => f(`storyParas.${i}`, `Our Story — paragraph ${i + 1}`, "textarea")),
      f("ctaEyebrow", "Discover — small line"),
      f("ctaH2", "Discover — heading"),
      f("ctaParas.0", "Discover — paragraph", "textarea"),
      f("ctaLabel", "Discover — button text"),
    ],
  },
  {
    key: "faqs",
    label: "FAQs",
    fields: [
      f("heroEyebrow", "Small line above the headline"),
      f("heroH1", "Headline"),
      f("heroParas.0", "Intro paragraph", "textarea"),
      ...[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19].flatMap((i) => [
        f(`items.${i}.q`, `Question ${i + 1}`),
        f(`items.${i}.a`, `Answer ${i + 1}`, "textarea"),
      ]),
      f("ctaHeading", "Bottom — heading"),
      f("ctaLead", "Bottom — paragraph", "textarea"),
      f("ctaLabel", "Bottom — button text"),
    ],
  },
];

const get = (obj, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
const set = (obj, path, val) => {
  const keys = path.split(".");
  const last = keys.pop();
  let t = obj;
  for (const k of keys) {
    if (t[k] == null) t[k] = /^\d+$/.test(k) ? [] : {};
    t = t[k];
  }
  t[last] = val;
};

export default function ContentEditor() {
  const content = useContent();
  const refresh = useContentRefresh();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: draft }),
      });
      if (!res.ok) throw new Error("save failed");
      toast.success("Saved — the website is updated.");
      await refresh();
      setDraft(null);
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/content`, { method: "DELETE", credentials: "include" });
      await refresh();
      setDraft(null);
      toast.success("Original text restored.");
    } catch {
      toast.error("Could not restore.");
    } finally {
      setSaving(false);
    }
  };

  if (!draft) {
    return (
      <>
        <PageVideoCard
          contentKey="weddingsVideo"
          title="Wedding film on the Weddings page"
          blurb="Upload your wedding film and it appears in the video section of the Weddings page — visitors press play themselves. MP4 or MOV, up to 500MB."
          headingDefault="Watch a Real Collanmore Wedding"
          eyebrowDefault="A Real Wedding on the Island"
          testPrefix="weddings-video"
        />
        <PageVideoCard
          contentKey="henVideo"
          title="Hen party film on the Hen Parties page"
          blurb="Upload your hen party film and it appears in the video section of the Hen Parties page. MP4 or MOV, up to 500MB."
          headingDefault="Watch a Collanmore Hen Party"
          eyebrowDefault="A Real Hen Party on the Island"
          testPrefix="hen-video"
        />
        <div className="mt-8 border border-ivory/15 bg-navy-2 p-8 md:p-10" data-testid="content-editor-intro">
        <h2 className="font-display text-3xl text-ivory">Edit the website's words</h2>
        <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-ivory/70">
          Change any headline, paragraph, feature line, experience card or guest review — your
          changes go live on the website the moment you save.
        </p>
        <button
          onClick={() => setDraft(JSON.parse(JSON.stringify(content)))}
          data-testid="content-edit-button"
          className="mt-6 bg-gold px-8 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2"
        >
          Start editing
        </button>
        </div>
      </>
    );
  }

  const change = (path) => (e) => {
    const val = e.target.value;
    setDraft((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      set(copy, path, val);
      return copy;
    });
  };

  const renderField = (field, prefix = "") => {
    const path = prefix + field.path;
    const value = get(draft, path) ?? "";
    const cls = "mt-2 w-full border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold";
    return (
      <div key={path}>
        <label className="block text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">
          {field.label}
        </label>
        {field.type === "textarea" ? (
          <textarea rows="3" value={value} onChange={change(path)} data-testid={`field-${path}`} className={`${cls} resize-none`} />
        ) : (
          <input value={value} onChange={change(path)} data-testid={`field-${path}`} className={cls} />
        )}
      </div>
    );
  };

  return (
    <>
      <PageVideoCard
        contentKey="weddingsVideo"
        title="Wedding film on the Weddings page"
        blurb="Upload your wedding film and it appears in the video section of the Weddings page — visitors press play themselves. MP4 or MOV, up to 500MB."
        headingDefault="Watch a Real Collanmore Wedding"
        eyebrowDefault="A Real Wedding on the Island"
        testPrefix="weddings-video"
      />
      <PageVideoCard
        contentKey="henVideo"
        title="Hen party film on the Hen Parties page"
        blurb="Upload your hen party film and it appears in the video section of the Hen Parties page. MP4 or MOV, up to 500MB."
        headingDefault="Watch a Collanmore Hen Party"
        eyebrowDefault="A Real Hen Party on the Island"
        testPrefix="hen-video"
      />
      <div className="mt-8" data-testid="content-editor">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button"
          className="bg-gold px-8 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save & publish to site"}
        </button>
        <button
          onClick={() => setDraft(null)}
          data-testid="content-cancel-button"
          className="border border-ivory/25 px-6 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:text-gold-2"
        >
          Cancel
        </button>
        <button
          onClick={reset}
          disabled={saving}
          data-testid="content-reset-button"
          className="border border-red-400/50 px-6 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-red-300 transition-colors hover:bg-red-400/10"
        >
          Restore original
        </button>
      </div>

      {SECTIONS.map((sec) => (
        <details key={sec.key} className="group mt-4 border border-ivory/15 bg-navy-2">
          <summary
            className="cursor-pointer list-none px-6 py-4 font-display text-xl text-ivory"
            data-testid={`content-section-${sec.key}`}
          >
            {sec.label}
            <span className="float-right text-gold-2 transition-transform duration-300 group-open:rotate-45">+</span>
          </summary>
          <div className="flex flex-col gap-4 border-t border-ivory/10 p-6">
            {sec.repeat
              ? (draft[sec.key] || []).map((item, i) => (
                  <div key={i} className="border border-ivory/10 p-4">
                    <p className="text-[10px] uppercase tracking-widest2 text-gold-2">Card {i + 1}</p>
                    <div className="mt-3 flex flex-col gap-4">{sec.fields.map((fl) => renderField(fl, `${sec.key}.${i}.`))}</div>
                  </div>
                ))
              : sec.fields.map((fl) => renderField(fl, `${sec.key}.`))}
          </div>
        </details>
      ))}
      </div>
    </>
  );
}
